import { isNativeBanculem, nativeNotificationCommand, type NativeNotificationState } from "./native-app";

const ENABLED_KEY = "banculem:system-notifications:v1";
const FID_KEY = "banculem:notification-fid:v1";
const DEVICE_KEY = "banculem:notification-device:v1";
const TIME_ZONE = "America/Ciudad_Juarez";

export type BanculemNotificationAction = "home" | "movements" | "update-yield" | "card-payment" | "register" | "savings";
export type BanculemNotificationKind = "expense" | "period" | "yield" | "test";

export interface BanculemNotificationCopy {
  title: string;
  body: string;
  action: BanculemNotificationAction;
  url: string;
}

export function notificationCopy(kind: BanculemNotificationKind): BanculemNotificationCopy {
  if (kind === "expense") return { title: "Gasto registrado", body: "Compra o gasto capturado correctamente.", action: "movements", url: "/?notification=movements" };
  if (kind === "period") return { title: "Quincena creada", body: "Quincena creada correctamente.", action: "home", url: "/?notification=home" };
  if (kind === "yield") return { title: "Actualiza tus rendimientos", body: "Toca para actualizar tus rendimientos.", action: "update-yield", url: "/?notification=update-yield" };
  return { title: "Notificaciones activadas", body: "Las notificaciones están listas en este dispositivo.", action: "home", url: "/?notification=home" };
}

let nativeState: NativeNotificationState | undefined;
let nativeStateUnavailable = false;

function acceptNativeState(value: Record<string, unknown>): void {
  if (!["setup", "denied", "error", "enabled", "pending", "disabled", "stale"].includes(String(value.state))
    || typeof value.enabled !== "boolean" || typeof value.configured !== "boolean") {
    throw new Error("Actualiza la app instalada para configurar los avisos.");
  }
  nativeState = value as unknown as NativeNotificationState;
  nativeStateUnavailable = false;
}

export async function refreshSystemNotificationStatus(): Promise<void> {
  if (!isNativeBanculem()) return;
  try { acceptNativeState(await nativeNotificationCommand("Status")); }
  catch { nativeState = undefined; nativeStateUnavailable = true; }
}

export function systemNotificationsSupported(): boolean {
  if (isNativeBanculem()) return Boolean(nativeState?.configured);
  return typeof Notification !== "undefined" && "serviceWorker" in navigator;
}

export function systemNotificationsEnabled(storage: Pick<Storage, "getItem"> = localStorage): boolean {
  if (isNativeBanculem()) return nativeState?.enabled === true;
  return systemNotificationsSupported()
    && Notification.permission === "granted"
    && storage.getItem(ENABLED_KEY) === "true";
}

export function systemNotificationStatus(): string {
  if (isNativeBanculem()) {
    if (nativeStateUnavailable) return "Actualiza la app instalada para configurar los avisos";
    if (!nativeState) return "Comprobando notificaciones del iPhone…";
    const labels: Record<NativeNotificationState["state"], string> = {
      setup: "Actualiza la app instalada para usar avisos locales",
      denied: "Permítelas en Ajustes del iPhone → Banculem → Notificaciones",
      error: "No se pudieron actualizar los avisos. Abre Banculem con conexión",
      pending: "Actualizando los avisos del iPhone",
      enabled: "Avisos locales activados en este iPhone",
      stale: "Avisos locales activados; falta actualizar las fechas con conexión",
      disabled: "Desactivadas en este iPhone",
    };
    return labels[nativeState.state];
  }
  if (!systemNotificationsSupported()) return "No disponibles en este navegador";
  if (Notification.permission === "denied") return "Bloqueadas en los ajustes del dispositivo";
  if (systemNotificationsEnabled()) return "Activadas en este dispositivo";
  return "Desactivadas en este dispositivo";
}

async function notificationDeviceId(fid: string): Promise<string> {
  const bytes = new TextEncoder().encode(fid);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("").slice(0, 40);
}

async function saveRegistration(uid: string, fid: string): Promise<void> {
  const [{ doc, setDoc }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
  const deviceId = await notificationDeviceId(fid);
  await setDoc(doc(firestore, "users", uid, "notificationDevices", deviceId), {
    fid,
    enabled: true,
    expenseConfirmations: true,
    periodConfirmations: true,
    dailyYieldReminder: true,
    cardPaymentReminders: true,
    timezone: TIME_ZONE,
    updatedAt: new Date().toISOString(),
  });
  localStorage.setItem(FID_KEY, fid);
  localStorage.setItem(DEVICE_KEY, deviceId);
}

async function registerWithMessaging(uid: string): Promise<void> {
  const [{ getMessaging, isSupported, onRegistered, register }, { firebaseApp }, serviceWorkerRegistration] = await Promise.all([
    import("firebase/messaging"),
    import("./firebase"),
    navigator.serviceWorker.ready,
  ]);
  if (!(await isSupported())) throw new Error("Este dispositivo no admite notificaciones de Banculem.");
  const messaging = getMessaging(firebaseApp);
  const vapidKey = String(import.meta.env.VITE_FIREBASE_VAPID_KEY ?? "").trim();

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      reject(new Error("Firebase tardó demasiado en registrar este dispositivo."));
    }, 15_000);
    const unsubscribe = onRegistered(messaging, (fid) => {
      if (settled) return;
      void saveRegistration(uid, fid).then(() => {
        settled = true;
        window.clearTimeout(timeout);
        unsubscribe();
        resolve();
      }, (error) => {
        settled = true;
        window.clearTimeout(timeout);
        unsubscribe();
        reject(error);
      });
    });
    register(messaging, {
      serviceWorkerRegistration,
      ...(vapidKey ? { vapidKey } : {}),
    }).catch((error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      unsubscribe();
      reject(error);
    });
  });
}

export async function enableSystemNotifications(uid: string): Promise<void> {
  if (isNativeBanculem()) { acceptNativeState(await nativeNotificationCommand("Enable")); return; }
  if (!systemNotificationsSupported()) throw new Error("Instala Banculem en la pantalla de inicio para activar notificaciones.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("No se concedió permiso para mostrar notificaciones.");
  await registerWithMessaging(uid);
  localStorage.setItem(ENABLED_KEY, "true");
}

export async function refreshSystemNotificationRegistration(uid: string): Promise<void> {
  if (isNativeBanculem()) {
    try { acceptNativeState(await nativeNotificationCommand("Refresh")); }
    catch { await refreshSystemNotificationStatus(); }
    return;
  }
  if (!systemNotificationsEnabled()) return;
  await registerWithMessaging(uid);
}

export async function disableSystemNotifications(uid: string): Promise<void> {
  if (isNativeBanculem()) { acceptNativeState(await nativeNotificationCommand("Disable")); return; }
  const deviceId = localStorage.getItem(DEVICE_KEY);
  if (deviceId) {
    const [{ doc, setDoc }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
    await setDoc(doc(firestore, "users", uid, "notificationDevices", deviceId), {
      enabled: false,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
  }
  localStorage.setItem(ENABLED_KEY, "false");
}

export async function showSystemNotification(kind: BanculemNotificationKind, id: string): Promise<boolean> {
  if (isNativeBanculem()) {
    if (kind === "yield") return false; // Scheduled locally by iOS from a fresh, read-only payment projection.
    const result = kind === "test" ? await nativeNotificationCommand("Test") : await nativeNotificationCommand("Confirm", kind);
    return result.ok === true;
  }
  if (!systemNotificationsEnabled()) return false;
  const copy = notificationCopy(kind);
  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification(copy.title, {
    body: copy.body,
    icon: "/icons/banculem.svg",
    badge: "/icons/banculem.svg",
    tag: `banculem-${kind}-${id}`,
    data: { action: copy.action, url: copy.url },
  });
  return true;
}

export function notificationActionFromUrl(url = window.location.href): BanculemNotificationAction | null {
  const value = new URL(url).searchParams.get("notification");
  if (value === "movements" || value === "update-yield" || value === "home" || value === "card-payment" || value === "register" || value === "savings") return value;
  return null;
}

// Coalesce Firestore listeners after a committed action; never use an optimistic financial snapshot.
let nativeRefreshTimer: ReturnType<typeof setTimeout> | undefined;
export function queueNativeNotificationRefresh(uid: string): void {
  if (!isNativeBanculem()) return;
  clearTimeout(nativeRefreshTimer);
  nativeRefreshTimer = setTimeout(() => { void refreshSystemNotificationRegistration(uid).catch(() => undefined); }, 800);
}
